<script>
  import { createEventDispatcher, tick } from "svelte";
  import { pannable } from "../actions/pannable.js";

  export let canEdit = false;
  export let canSave = false;
  export let isSaving = false;
  export let hasSelection = false;
  export let canUndo = false;
  export let canRedo = false;

  export let pdfRenderScale = 0.75;
  export let zoomLevel = 1.0;
  export let hideObjectBorders = false;
  export let imageCompressQuality = 0.8;
  export let isMultiSelectMode = false;

  let isPinned = true;
  let isAdvancedOpen = false;
  let toolbarEl;
  let toolbarX = 0;
  let toolbarY = 0;

  async function togglePin() {
    isPinned = !isPinned;
    if (!isPinned && toolbarEl) {
      const rect = toolbarEl.getBoundingClientRect();
      toolbarX = rect.left;
      toolbarY = rect.top;
    }
  }

  function handlePanMove(e) {
    if (isPinned) return;
    toolbarX += e.detail.dx;
    toolbarY += e.detail.dy;
  }

  const SCALES = [0.25, 0.5, 0.75, 1, 1.5];

  const dispatch = createEventDispatcher();

  let pdfInput;
  let imageInput;
  let compressImage = true;

  function zoomOut() {
    dispatch("changeZoomLevel", Math.max(0.1, zoomLevel - 0.1));
  }

  function zoomIn() {
    dispatch("changeZoomLevel", Math.min(5.0, zoomLevel + 0.1));
  }

  function uploadPdf(event) {
    const files =
      event.target.files || (event.dataTransfer && event.dataTransfer.files);
    const file = files && files[0];
    if (!file) return;
    dispatch("uploadPdf", { file });
    event.target.value = null;
  }

  function uploadImage(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    dispatch("uploadImage", {
      file,
      compress: compressImage,
      quality: imageCompressQuality,
    });
    event.target.value = null;
  }

  function rename(event) {
    dispatch("rename", { value: event.target.value });
  }

  function triggerPdf() {
    if (pdfInput) pdfInput.click();
  }

  function triggerImage() {
    if (!canEdit) return;
    if (imageInput) imageInput.click();
  }
</script>

<div
  id="app-toolbar"
  bind:this={toolbarEl}
  class={isPinned
    ? "sticky top-2 mx-auto z-20 w-[96%] md:w-11/12 max-w-5xl transition-all duration-300"
    : "fixed z-50 min-w-[max-content]"}
  style={isPinned ? "" : `left: ${toolbarX}px; top: ${toolbarY}px;`}
  use:pannable
  on:panmove={handlePanMove}
>
  <div
    class="glass border-gray-200/50 rounded-xl md:rounded-2xl shadow-soft px-3 py-1.5 md:px-4 md:py-2 flex flex-col gap-1.5 md:gap-2 {isPinned
      ? ''
      : 'cursor-move'}"
  >
    <div
      class="flex flex-wrap items-center justify-between gap-2 overflow-x-auto pb-1"
      style="scrollbar-width: none; -ms-overflow-style: none;"
    >
      <style>
        #app-toolbar div::-webkit-scrollbar {
          display: none;
        }
      </style>
      <div class="flex items-center gap-1 flex-shrink-0">
        <input
          bind:this={pdfInput}
          type="file"
          accept="application/pdf"
          class="hidden"
          on:change={uploadPdf}
        />
        <button
          type="button"
          on:click={triggerPdf}
          class="h-9 px-4 md:h-10 md:px-5 mr-1 md:mr-2 rounded-lg md:rounded-xl bg-indigo-600 shadow-glow text-white text-[10px] md:text-xs font-semibold btn-hover hover:bg-indigo-700 transition-all focus:outline-none whitespace-nowrap"
        >
          載入PDF
        </button>

        <div class="w-px h-8 bg-gray-200/80 mx-1"></div>

        <button
          type="button"
          on:click={() => canEdit && dispatch("addText")}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none"
          class:opacity-50={!canEdit}
          class:cursor-not-allowed={!canEdit}
          aria-label="Add text"
        >
          <img src="/notes.png" alt="" class="w-5 h-5 opacity-80" />
          <span class="text-[9px] font-bold mt-0.5 text-gray-600 tracking-wide"
            >文字</span
          >
        </button>

        <input
          bind:this={imageInput}
          type="file"
          accept="image/*"
          class="hidden"
          on:change={uploadImage}
        />
        <button
          type="button"
          on:click={triggerImage}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none"
          class:opacity-50={!canEdit}
          class:cursor-not-allowed={!canEdit}
          aria-label="Add image"
        >
          <img src="/image.png" alt="" class="w-5 h-5 opacity-80" />
          <span class="text-[9px] font-bold mt-0.5 text-gray-600 tracking-wide"
            >圖片</span
          >
        </button>

        <button
          type="button"
          on:click={() => canEdit && dispatch("addDrawing")}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none"
          class:opacity-50={!canEdit}
          class:cursor-not-allowed={!canEdit}
          aria-label="Add drawing"
        >
          <img src="/gesture.png" alt="" class="w-5 h-5 opacity-80" />
          <span class="text-[9px] font-bold mt-0.5 text-gray-600 tracking-wide"
            >塗鴉</span
          >
        </button>

        <button
          type="button"
          on:click={() => canEdit && dispatch("addShape")}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none"
          class:opacity-50={!canEdit}
          class:cursor-not-allowed={!canEdit}
          title="Add shape"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            class="text-gray-700 opacity-80"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="3" width="8" height="8" rx="2" />
            <circle cx="17" cy="17" r="5" />
            <path d="M12 4 L20 12 L12 20 L4 12 Z" fill="none" />
          </svg>
          <span class="text-[9px] font-bold mt-0.5 text-gray-600 tracking-wide"
            >形狀</span
          >
        </button>

        <div class="w-px h-8 bg-gray-200/80 mx-1"></div>

        <button
          type="button"
          on:click={() => dispatch("toggleMultiSelect")}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none {isMultiSelectMode
            ? 'bg-indigo-100 text-indigo-700 shadow-inner'
            : ''}"
          aria-label="Toggle Multi-select"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class={isMultiSelectMode
              ? "text-indigo-600"
              : "text-gray-700 opacity-80"}
          >
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          <span
            class="text-[9px] mt-0.5 tracking-wide {isMultiSelectMode
              ? 'text-indigo-700 font-bold'
              : 'text-gray-600 font-bold'}">多選</span
          >
        </button>

        <div class="w-px h-8 bg-gray-200/80 mx-1"></div>

        <button
          type="button"
          on:click={() => dispatch("undo")}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none"
          class:opacity-40={!canUndo}
          class:cursor-not-allowed={!canUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gray-700 opacity-80"
            ><path d="M3 7v6h6" /><path
              d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"
            /></svg
          >
          <span class="text-[9px] font-bold mt-0.5 text-gray-600 tracking-wide"
            >復原</span
          >
        </button>

        <button
          type="button"
          on:click={() => dispatch("redo")}
          class="flex flex-col items-center justify-center w-11 h-11 md:w-12 md:h-12 btn-hover rounded-xl transition-colors focus:outline-none"
          class:opacity-40={!canRedo}
          class:cursor-not-allowed={!canRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gray-700 opacity-80"
            ><path d="M21 7v6h-6" /><path
              d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3 2.7"
            /></svg
          >
          <span class="text-[9px] font-bold mt-0.5 text-gray-600 tracking-wide"
            >重做</span
          >
        </button>

        {#if hasSelection}
          <div class="w-px h-8 bg-gray-200/80 mx-1"></div>
          <div class="flex items-center gap-0.5">
            <button
              on:click={() => dispatch("bringToFront")}
              class="w-8 h-8 md:w-9 md:h-9 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors"
              title="Top"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-gray-700 opacity-80"
                ><path d="m18 15-6-6-6 6" /><path d="m18 9-6-6-6 6" /></svg
              >
            </button>
            <button
              on:click={() => dispatch("moveForward")}
              class="w-8 h-8 md:w-9 md:h-9 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors"
              title="Up"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-gray-700 opacity-80"
                ><path d="m18 15-6-6-6 6" /></svg
              >
            </button>
            <button
              on:click={() => dispatch("moveBackward")}
              class="w-8 h-8 md:w-9 md:h-9 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors"
              title="Down"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-gray-700 opacity-80"><path d="m6 9 6 6 6-6" /></svg
              >
            </button>
            <button
              on:click={() => dispatch("sendToBack")}
              class="w-8 h-8 md:w-9 md:h-9 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors"
              title="Btm"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-gray-700 opacity-80"
                ><path d="m6 9 6 6 6-6" /><path d="m6 15 6 6 6-6" /></svg
              >
            </button>
            <button
              on:click={() => dispatch("clone")}
              class="w-8 h-8 md:w-9 md:h-9 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors"
              title="Copy"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-indigo-600 opacity-90"
                ><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path
                  d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"
                /></svg
              >
            </button>
            <button
              on:click={() => dispatch("delete")}
              class="w-8 h-8 md:w-9 md:h-9 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors text-red-500 hover:bg-red-50"
              title="Delete"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                ><path d="M3 6h18" /><path
                  d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"
                /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></svg
              >
            </button>
          </div>
        {/if}
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          on:click={togglePin}
          class="w-8 h-8 md:w-10 md:h-10 flex flex-col items-center justify-center btn-hover rounded-xl transition-colors border {isPinned
            ? 'text-indigo-600 border-indigo-200 bg-indigo-50'
            : 'text-gray-500 border-transparent hover:bg-gray-100'}"
          title={isPinned ? "解開釘選 (可拖移)" : "釘選至頂部"}
        >
          {#if isPinned}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="currentColor"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              ><path d="M12 17v5" /><path
                d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
              /></svg
            >
          {:else}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              ><path d="M12 17v5" /><path
                d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z"
              /></svg
            >
          {/if}
        </button>
        <button
          type="button"
          on:click={() => (isAdvancedOpen = !isAdvancedOpen)}
          class="w-8 h-8 md:w-10 md:h-10 flex flex-col items-center justify-center btn-hover rounded-xl transition-all border border-gray-200 {isAdvancedOpen
            ? 'bg-gray-100 text-gray-800'
            : 'bg-transparent text-gray-500'}"
          title="進階設定"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="transition-transform duration-300 {isAdvancedOpen
              ? 'rotate-180'
              : ''}"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <button
          type="button"
          on:click={() => dispatch("save")}
          class="h-9 px-4 md:h-10 md:px-5 rounded-lg md:rounded-xl bg-indigo-500 text-white text-[10px] md:text-xs font-semibold hover:bg-indigo-600 hover:-translate-y-0.5 shadow-md hover:shadow-lg transition-all focus:outline-none whitespace-nowrap"
          class:opacity-50={!canSave}
          class:cursor-not-allowed={!canSave}
          disabled={!canSave}
        >
          {isSaving ? "儲存中…" : "儲存"}
        </button>
      </div>
    </div>

    {#if isAdvancedOpen}
      <div
        class="flex flex-wrap items-center justify-center lg:justify-end gap-x-4 gap-y-2 pt-1.5 border-t border-gray-100 flex-shrink-0 transition-opacity"
      >
        <div
          class="flex items-center gap-1 rounded bg-white/40 px-2 py-1 shadow-sm border border-gray-100"
        >
          <span
            class="text-xs font-semibold text-gray-500 tracking-wide mr-1 whitespace-nowrap"
            title="Zoom">顯示比例</span
          >
          <button
            on:click={zoomOut}
            class="w-5 h-5 flex items-center justify-center rounded bg-gray-100/80 hover:bg-gray-200 text-gray-700 transition-colors font-bold focus:outline-none"
            title="Zoom Out (縮小)">−</button
          >
          <select
            class="h-6 rounded-md border-none bg-transparent px-1 text-xs font-semibold text-gray-700 focus:ring-0 outline-none transition-colors text-center appearance-none"
            value={zoomLevel}
            on:change={(e) =>
              dispatch("changeZoomLevel", parseFloat(e.target.value))}
          >
            <option value={0.25}>25%</option>
            <option value={0.5}>50%</option>
            <option value={0.65}>65%</option>
            <option value={0.8}>80%</option>
            <option value={1.0}>100%</option>
            <option value={1.5}>150%</option>
            <option value={2.0}>200%</option>
            <option value={3.0}>300%</option>
          </select>
          <button
            on:click={zoomIn}
            class="w-5 h-5 flex items-center justify-center rounded bg-gray-100/80 hover:bg-gray-200 text-gray-700 transition-colors font-bold focus:outline-none"
            title="Zoom In (放大)">+</button
          >
        </div>

        <div
          class="flex items-center gap-1 rounded bg-white/40 px-2 py-1 shadow-sm border border-gray-100"
        >
          <span
            class="text-xs font-semibold text-gray-500 tracking-wide mr-1 whitespace-nowrap"
            title="Render Resolution">預覽解析度</span
          >
          <select
            class="h-6 rounded-md border-none bg-transparent px-1 text-xs font-semibold text-gray-700 focus:ring-0 outline-none transition-colors"
            value={pdfRenderScale}
            on:change={(e) =>
              dispatch("changeRenderScale", parseFloat(e.target.value))}
          >
            <option value={0.25}>25% (Faster)</option>
            <option value={0.5}>50%</option>
            <option value={0.75}>75% (Default)</option>
            <option value={1}>100%</option>
            <option value={1.5}>150% (HD)</option>
          </select>
        </div>

        <label
          class="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50/50 transition-colors px-2 py-1 rounded bg-white/40 shadow-sm border border-gray-100"
          title="Compress imported image"
        >
          <input
            type="checkbox"
            bind:checked={compressImage}
            class="rounded text-indigo-600 focus:ring-indigo-500 w-3 h-3"
          />
          <span
            class="text-[10px] md:text-xs font-semibold text-gray-600 truncate tracking-wide"
            >壓縮圖片</span
          >
          {#if compressImage}
            <input
              type="number"
              min="0.1"
              max="1.0"
              step="0.1"
              class="h-5 w-10 text-[10px] object-cover text-center border-b border-gray-300/50 bg-transparent rounded-none focus:outline-none"
              value={imageCompressQuality}
              on:input={(e) =>
                dispatch(
                  "changeCompressQuality",
                  parseFloat(e.target.value) || 0.8,
                )}
            />
          {/if}
        </label>

        <label
          class="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50/50 transition-colors px-2 py-1 rounded bg-white/40 shadow-sm border border-gray-100"
          title="隱藏頁面上新增項目的調整功能外框"
        >
          <input
            type="checkbox"
            checked={hideObjectBorders}
            on:change={(e) => dispatch("changeHideBorders", e.target.checked)}
            class="rounded text-indigo-600 focus:ring-indigo-500 w-3 h-3"
          />
          <span
            class="text-[10px] md:text-xs font-semibold text-gray-600 tracking-wide whitespace-nowrap"
            >隱藏選取框</span
          >
        </label>
      </div>
    {/if}
  </div>
</div>
